import React, { useState, useEffect, createContext, useContext } from 'react'
import {
  ChannelFieldsFragment,
  EventQueryResult,
  useLeaveEventMutation,
  EventUserPlanQueryResult,
  BaseProfileFieldsFragment,
  BroadcastVideoLayout
} from 'graphql/generated'
import { useCurrentUser } from 'hooks'
import { useConfirmPromptModal } from 'components/modals/ConfirmPromptModal'
import { useRouter } from 'next/router'
import { useMeetingManager } from 'amazon-chime-sdk-component-library-react'
import { useGetAttendee } from 'hooks/chime/useGetAttendee'
import useChimeMeeting from 'hooks/chime/useChimeMeeting'

export interface EventVideoContextShape {
  event: EventQueryResult['event']
  plan?: NonNullable<EventUserPlanQueryResult['event']['userPlan']>
  channel?: Maybe<ChannelFieldsFragment>
  mainChannel: Maybe<ChannelFieldsFragment>
  currentSpeakers: Maybe<BaseProfileFieldsFragment[]>
  currentLayout: BroadcastVideoLayout
  isBroadcastRole: boolean
  leaveEvent: Callback

  showGuestList: boolean
  setShowGuestList: CallbackWithParam<boolean>
  showDJControls: boolean
  setShowDJControls: CallbackWithParam<boolean>
  recordingConsented: boolean
  setRecordingConsented: CallbackWithParam<boolean>

  joinMeeting: Callback
}

interface EventVideoProviderProps {
  event: EventQueryResult['event']
  plan?: NonNullable<EventUserPlanQueryResult['event']['userPlan']>
  children?: React.ReactNode
}

const EventVideoContext = createContext<EventVideoContextShape>(
  {} as EventVideoContextShape
)

export const useEventVideoContext = () => useContext(EventVideoContext)

export function EventVideoProvider({
  event,
  plan,
  children
}: EventVideoProviderProps) {
  const [showGuestList, setShowGuestList] = useState(false)
  const [showDJControls, setShowDJControls] = useState(false)
  const [recordingConsented, setRecordingConsented] = useState(false)
  const {
    currentChannel: channel,
    mainChannel,
    currentSpeakers,
    currentLayout = BroadcastVideoLayout.HOST
  } = plan || {}

  const { id: currentUserId } = useCurrentUser()
  const isBroadcastRole =
    // current user is a guest speaker
    currentSpeakers?.some(x => x.id === currentUserId) ||
    // current user is the host, during non-presenter-only mode
    (event.isOwner && currentLayout !== BroadcastVideoLayout.PRESENTER)

  const router = useRouter()
  const [leaveEventMutation] = useLeaveEventMutation({
    variables: { eventId: event.id }
  })
  const confirm = useConfirmPromptModal()

  const meetingManager = useMeetingManager()
  const leaveEvent = () =>
    confirm({
      title: 'Leave Event?',
      body: `Are you sure you wish to leave ${event.title}?`,
      cancelLabel: 'No',
      confirmLabel: 'Yes',
      onClick: () => {
        window.analytics.track('Left event', {
          eventId: event.id,
          eventName: event.title
        })
        leaveEventMutation()
        meetingManager.leave()
        router.push(event.publicUrl)
      }
    })

  const getAttendee = useGetAttendee()
  useEffect(() => {
    meetingManager.getAttendee = getAttendee // how the AWS can fetch our member

    return () => {
      meetingManager.leave()
    }
    // eslint-disable-next-line
  }, [])

  const channelSessionId = mainChannel?.sessionId
  const channelToken = mainChannel?.token
  const { joinMeeting } = useChimeMeeting(channelSessionId, channelToken)

  return (
    <EventVideoContext.Provider
      value={{
        event,
        plan,
        channel,
        mainChannel,
        currentSpeakers,
        currentLayout,
        isBroadcastRole,
        leaveEvent,
        showGuestList,
        setShowGuestList,
        showDJControls,
        setShowDJControls,
        recordingConsented,
        setRecordingConsented,
        joinMeeting
      }}
    >
      {children}
    </EventVideoContext.Provider>
  )
}
